<template>
  <div>
    <InsightsUnusualActivityWidgetContainer
      ref="uaContainer"
      :key="hash"
      v-slot="{
        confidenceSummary,
        currentDashboardUser,
        data,
        insightsAvailable,
        isFirstLoad,
        isLoading,
        monthlyTotal,
        currentNoFiltersTotal,
        pagination,
        period,
        setPage,
        sort,
        sortOptions,
        totalInstances,
        totalMembers,
        totalDuration,
        totalInstancesByConfidenceLevels,
        previous60DaysTotalDuration,
        filterStats,
        hasData,
      }"
      :widget-key="widgetKey"
    >
      <InsightsUnusualActivityUpsellBanner v-if="hasNoInsightsUaPreview" />

      <h3
        v-if="hasNoInsightsUaPreview"
        class="unusual-activity-section-title mt-30 mb-10"
      >
        Instances in the last 60 days
      </h3>

      <InsightsUnusualActivityConfidencePills
        v-if="filterStats"
        :confidence-counts="filterStats.confidence_levels"
        :show-counts="!userId"
        :is-loading="isLoading"
        :interactive="!hasNoInsightsUaPreview"
        @click="setConfidenceLevelsFilter($event)"
      />

      <div class="d-flex justify-space-between mt-10 flex-wrap">
        <InsightsUnusualActivityWidgetSummary
          :is-loading="isLoading"
          :user-view="!!userId"
          :total-instances-by-confidence-levels="totalInstancesByConfidenceLevels"
          :total-instances="totalInstances"
          :total-members="totalMembers"
          :total-duration="totalDuration"
          :previous-days-total-duration="previous60DaysTotalDuration"
        />
        <InsightsUnusualActivityWidgetConfidenceSummary
          v-if="confidenceSummary?.reasons"
          :confidence-summary="confidenceSummary"
          :total-instances-by-confidence-levels="totalInstancesByConfidenceLevels"
          :current-dashboard-user="currentDashboardUser || {}"
          :time-zone="timeZone"
          :is-loading="isLoading"
        />
      </div>
      <template v-if="hasNoInsightsUaPreview">
        <h3 class="unusual-activity-section-title mt-30 mb-10">Members with unusual activity</h3>
        <InsightsUnusualActivityFakeTable v-if="isLoading || totalMembers > 0" />
        <InsightsNoData
          v-else-if="!isLoading"
          class="widget-wrapper py-20"
        >
          <template #title>
            <div class="bold">That's good news!</div>
          </template>
          <template #description>
            <span>No unusual activity was recorded<br />in the last 60 days.</span>
          </template>
        </InsightsNoData>
      </template>
      <InsightsUnusualActivityCTADialog v-else-if="!insightsAvailable">
        <InsightsUnusualActivityMembersTableBlur />
      </InsightsUnusualActivityCTADialog>
      <div v-else>
        <InsightsUnusualActivityWidget
          v-if="hasData || isLoading"
          :time-zone="timeZone"
          :data="data"
          :period="period"
          :is-loading="isLoading"
          :first-load="isFirstLoad"
          :pagination="pagination"
          :set-page="setPage"
          :sort="sort"
          :sort-options="sortOptions"
        />
        <InsightsNoData
          v-else-if="!isLoading"
          class="widget-wrapper py-20"
        >
          <template #title>
            <div class="bold">That's good news!</div>
          </template>
          <template #description>
            <span>No unusual activity was recorded for the selected time period</span>
          </template>
          <template
            v-if="monthlyTotal || currentNoFiltersTotal"
            #footer
          >
            <p v-if="!insightsAvailable">This member has unusual activity.</p>
            <InsightsUnusualActivityInvestigateNow
              border
              :mode="'advanced'"
              :monthly-total="monthlyTotal"
              :current-no-filters-total="currentNoFiltersTotal"
              :organization-insights-url="organizationInsightsUrl"
              :period="period"
              :query-date="queryDate"
              :user-id="userId"
              :team-id="teamId"
              class="mt-10"
            />
          </template>
        </InsightsNoData>
      </div>
    </InsightsUnusualActivityWidgetContainer>
  </div>
</template>

<script>
import InsightsNoData from '~/components/insights/InsightsNoData.vue';
import InsightsUnusualActivityCTADialog from '~/components/insights/InsightsUnusualActivityCTADialog.vue';
import InsightsUnusualActivityFakeTable from '~/components/insights/InsightsUnusualActivityFakeTable.vue';
import InsightsUnusualActivityMembersTableBlur from '~/components/insights/InsightsUnusualActivityMembersTableBlur.vue';
import InsightsUnusualActivityUpsellBanner from '~/components/insights/InsightsUnusualActivityUpsellBanner.vue';
import InsightsUnusualActivityWidget from '~/components/insights/InsightsUnusualActivityWidget.vue';
import InsightsUnusualActivityWidgetContainer from '~/components/insights/InsightsUnusualActivityWidgetContainer.vue';
import InsightsUnusualActivityWidgetConfidenceSummary from '~/components/insights/InsightsUnusualActivityWidgetConfidenceSummary.vue';
import InsightsUnusualActivityWidgetSummary from '~/components/insights/InsightsUnusualActivityWidgetSummary.vue';
import InsightsUnusualActivityConfidencePills from '~/components/insights/InsightsUnusualActivityConfidencePills.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import InsightsUnusualActivityInvestigateNow from '~/components/insights/InsightsUnusualActivityInvestigateNow.vue';
import { get, isEqual } from 'lodash-es';
import { unusualActivityWidgetKey } from '~/utils/insights_filter';
import { INSIGHTS } from '~/app_constants';
import InsightsFiltersMixin from '~/mixins/Insights/InsightsFiltersMixin';

const HASH_EXCLUDED_FILTERS = ['confidence_levels', 'index', 'starts_at', 'activity_type'];

export default {
  name: 'InsightsUnusualActivity',
  components: {
    InsightsNoData,
    InsightsUnusualActivityCTADialog,
    InsightsUnusualActivityConfidencePills,
    InsightsUnusualActivityFakeTable,
    InsightsUnusualActivityInvestigateNow,
    InsightsUnusualActivityMembersTableBlur,
    InsightsUnusualActivityUpsellBanner,
    InsightsUnusualActivityWidget,
    InsightsUnusualActivityWidgetConfidenceSummary,
    InsightsUnusualActivityWidgetContainer,
    InsightsUnusualActivityWidgetSummary,
  },
  mixins: [InsightsFiltersMixin],
  props: {
    timeZone: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters(['insightsAvailable', 'hasNoInsightsUaPreview', 'currentDashboardUser', 'filters', 'queryDate', 'userId']),
    ...mapState({
      organizationInsightsUrl: 'organization_insights_url',
      widgetData: (state) =>
        get(
          state.widgets,
          this.userId ? INSIGHTS.api.unusualActivity.individual : INSIGHTS.api.unusualActivity.team,
          {},
        ),
    }),
    hash() {
      return unusualActivityWidgetKey(this.filters, HASH_EXCLUDED_FILTERS);
    },
    teamId() {
      return this.filters?.team_id;
    },
    widgetKey() {
      return this.userId ? INSIGHTS.api.unusualActivity.individual : INSIGHTS.api.unusualActivity.team;
    },
  },
  watch: {
    'filters.confidence_levels'(newVal, oldVal) {
      if (isEqual(newVal, oldVal)) {
        return;
      }
      this.$nextTick(() => {
        this.$refs.uaContainer?.loadData({ page: 1 });
      });
    },
  },
  created() {
    this.toggleNavigationSidebar(false);
  },
  methods: mapActions(['toggleNavigationSidebar']),
};
</script>
